'use client';

import Link from 'next/link';
import dynamic from 'next/dynamic';
import type { WebsitePhoto } from '@/components/website-builder/website-layout';

/**
 * Tenant ordering site — DoorDash-Storefront UX skeleton in Sweetspot skin
 * (docs/blankstreet-doordash-storefront-teardown.md §3–6, plan M0).
 * One page: optional category nav → hero + info → menu sections →
 * item modal (option groups) → cart sheet → pickup checkout → offers land in
 * the seller's normal Sweetspot orders/messages.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  Clock,
  Instagram,
  Mail,
  MapPin,
  Minus,
  Phone,
  Plus,
  ShoppingBag,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { mergeSitePhotos, hasProductPrice, websiteLayoutSchema, type DesignTokens, type Section } from '@sweetspot/shared';
import { SecondaryProductCopy } from '@/components/storefront/secondary-product-copy';
import { StorefrontPromotions } from '@/components/storefront-builder/renderer';
import { siteApi, type PublicInstagramProductPhoto } from '@/lib/api';
import {
  responsiveProductImageAttributes,
  type ProductImageVariants,
} from '@/lib/product-image-variants';
import {
  orderCtaHref,
  orderCtaLabel,
  orderCtaOpensNewTab,
  orderingMode,
  readOrderCta,
  type OrderCta,
} from '@/lib/order-cta';
import { PaymentForm, paymentsAvailable, type PaymentFormHandle } from './payment-form';
import { LocationDialog } from './location-dialog';
import { MallowEditorialHero, isMallowEditorial } from './mallow-editorial-hero';
import { PublicInstagramGallery } from '@/components/instagram/public-instagram-gallery';
import { PublicInstagramComments } from '@/components/instagram/public-instagram-comments';
import { InstagramProductPhoto } from '@/components/instagram/instagram-product-photo';

/* ── Types (mirror /storefront/business/:slug/site-menu) ─────────────── */

export interface SiteOption {
  id: string;
  name: string;
  priceDelta: string | number;
  isDefault: boolean;
}
export interface SiteOptionGroup {
  id: string;
  name: string;
  required: boolean;
  minSelect: number;
  maxSelect: number;
  options: SiteOption[];
}
export interface SiteProduct {
  id: string;
  title: string;
  description?: string | null;
  name2?: string | null;
  description2?: string | null;
  price: string | number;
  category?: string | null;
  imageUrl?: string | null;
  imageVariants?: ProductImageVariants | null;
  instagramPhoto?: PublicInstagramProductPhoto | null;
  minQty?: number;
  optionGroups?: SiteOptionGroup[];
}
export interface SiteMenu {
  business: {
    id: string;
    name: string;
    slug: string | null;
    siteSlug?: string | null;
    siteDomain?: string | null;
    locationRegion?: string | null;
    bio: string | null;
    addressStreet: string | null;
    locationCity: string | null;
    handleInstagram: string | null;
    avgRating: number | null;
    ratingCount: number;
    siteConfig: Record<string, unknown> | null;
    /** Brand assets (dashboard → Storefront → Design → Brand). */
    coverImageUrl?: string | null;
    logoUrl?: string | null;
    logoMarkUrl?: string | null;
    acceptsCards?: boolean;
    orderingEnabled?: boolean;
  };
  websitePhotos?: WebsitePhoto[];
  categories: Array<{ name: string; products: SiteProduct[] }>;
  promotions: {
    tokens: DesignTokens;
    sections: Extract<Section, { type: 'announcement' }>[];
  } | null;
}

interface CartLine {
  key: string;
  productId: string;
  title: string;
  unitPrice: number;
  qty: number;
  optionIds: string[];
  optionLabels: string[];
  imageUrl?: string | null;
}

/* ── Helpers ──────────────────────────────────────────────────────────── */

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

function fmtTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${ampm}` : `${h12}:00 ${ampm}`.replace(':00 ', ' ');
}

function money(n: number): string {
  return `$${n.toFixed(2)}`;
}

function useOpenState(hours: Record<string, [string, string]> | null) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!hours) return { known: false, open: true, label: '' };
    const la = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const today = hours[DAY_KEYS[la.getDay()]];
    if (!today) return { known: true, open: false, label: 'Closed today' };
    const mins = la.getHours() * 60 + la.getMinutes();
    const [o, c] = today;
    const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
    if (mins < toMin(o)) return { known: true, open: false, label: `Opens ${fmtTime(o)}` };
    if (mins >= toMin(c)) return { known: true, open: false, label: `Closed · opens tomorrow` };
    return { known: true, open: true, label: `Open until ${fmtTime(c)}` };
  }, [hours, now]);
}

/* ── Main component ───────────────────────────────────────────────────── */

type SiteClientProps = { data: SiteMenu; handle: string; analyticsEnabled?: boolean; photoPreview?: boolean };
const WebsiteSite = dynamic(() => import('@/components/website-builder/website-site').then(module => module.WebsiteSite));
export function SiteClient(props: SiteClientProps) {
  const parsed = websiteLayoutSchema.safeParse(props.data.business.siteConfig?.websiteLayout);
  if (!parsed.success) return <SiteClientCore {...props} />;
  const hasMenu = parsed.data.sections.includes('menu');
  return <WebsiteSite doc={parsed.data} data={props.data} handle={props.handle}
    trackView={!hasMenu && props.analyticsEnabled !== false} preview={props.photoPreview}
    menu={hasMenu ? <SiteClientCore {...props} embedded /> : undefined} />;
}

function SiteClientCore({
  data,
  handle,
  analyticsEnabled = true,
  photoPreview = false,
  embedded = false,
}: {
  data: SiteMenu;
  handle: string;
  analyticsEnabled?: boolean;
  photoPreview?: boolean;
  embedded?: boolean;
}) {
  const { business, categories, promotions } = data;
  const pricedProductIds = useMemo(() => new Set(
    categories.flatMap((category) => category.products)
      .filter((product) => hasProductPrice(product.price))
      .map((product) => product.id),
  ), [categories]);
  // Preserve the exact route handle: a site's public handle/custom domain can
  // differ from its marketplace slug, and event/order endpoints resolve both.
  const slug = handle;
  const [photoDraft, setPhotoDraft] = useState<Record<string, unknown>>({});
  useEffect(() => {
    if (!photoPreview) return;
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.parent || event.data?.type !== 'site-photo-preview') return;
      try { setPhotoDraft({ ...Object.fromEntries(['heroImage', 'heroMobileImage', 'heroLayout', 'heroPosition', 'heroMobilePosition', 'photoBanner'].map(k => [k, null])), ...mergeSitePhotos({}, event.data.photos) }); } catch { /* ignore invalid drafts */ }
    };
    window.addEventListener('message', receive);
    window.parent.postMessage({ type: 'site-photo-preview-ready' }, window.location.origin);
    return () => window.removeEventListener('message', receive);
  }, [photoPreview]);
  const cfg = { ...(business.siteConfig ?? {}), ...photoDraft } as Record<string, any>;
  const showCategoryNav = cfg.showCategoryNav !== false;
  const heroImage = !embedded && typeof cfg.heroImage === 'string' ? cfg.heroImage : null;
  const heroMobileImage = typeof cfg.heroMobileImage === 'string' ? cfg.heroMobileImage : null;
  const mallowEditorial = Boolean(heroImage) && isMallowEditorial(business.id, embedded, photoPreview);
  const photoHero = Boolean(heroImage) && cfg.heroLayout === 'photo';
  const photoBanner = photoHero && cfg.photoBanner?.enabled !== false && typeof cfg.photoBanner?.image === 'string' ? cfg.photoBanner : null;
  const bannerCta = photoBanner?.buttonTarget !== 'menu' && photoBanner?.buttonUrl ? { type: 'link' as const, value: photoBanner.buttonUrl } : null;
  // Keep announcements and category controls in the normal page flow.
  const overlayHeader = mallowEditorial || (photoHero && !showCategoryNav && !promotions);
  const [hasScrolled, setHasScrolled] = useState(false);
  useEffect(() => {
    if (!overlayHeader) return;
    const update = () => setHasScrolled(window.scrollY > 16);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [overlayHeader]);
  const clearHeader = !mallowEditorial && overlayHeader && !hasScrolled;
  const brandFilter = overlayHeader
    ? `brightness-0 transition-[filter] duration-200 motion-reduce:transition-none ${clearHeader ? 'invert' : ''}`
    : '';
  // The retail face of the store. A business can sell wholesale under one name
  // (`business.name`, what buyers see in the marketplace) and run its own shop
  // under another — `siteConfig.siteName` is that second name.
  const displayName =
    typeof cfg.siteName === 'string' && cfg.siteName.trim() ? cfg.siteName.trim() : business.name;
  const theme = (cfg.theme ?? {}) as Record<string, any>;
  const vars = {
    '--s-bg': theme.bg ?? '#FAF7F0',
    '--s-surface': theme.surface ?? '#FEFCF7',
    '--s-ink': theme.ink ?? '#1D1A16',
    '--s-muted': theme.muted ?? '#8A8478',
    '--s-border': theme.border ?? '#E5DFD2',
    '--s-primary': theme.primary ?? '#1D1A16',
    '--s-primary-fg': theme.primaryFg ?? '#F6F2E9',
    '--s-radius': `${theme.radius ?? 10}px`,
  } as React.CSSProperties;
  const promotionVars = {
    '--sf-primary': 'var(--s-primary)',
    '--sf-on-primary': 'var(--s-primary-fg)',
    '--sf-bg': 'var(--s-bg)',
    '--sf-text': 'var(--s-ink)',
  } as React.CSSProperties;

  const hours = (cfg.hours ?? null) as Record<string, [string, string]> | null;
  const openState = useOpenState(hours);
  const storageKey = `ss_site_cart_${slug}`;

  // Order button (siteConfig.orderCta). A seller who takes orders elsewhere —
  // Instagram DM, phone, a Square/Toast link — gets a menu with one button
  // leading there: no cart, no checkout. Absent (or type "none") → Sweetspot
  // ordering exactly as before: card checkout when the seller accepts cards,
  // else pay at pickup.
  const acceptsCards = paymentsAvailable(Boolean(business.acceptsCards));
  const mode = orderingMode(cfg, acceptsCards, business.orderingEnabled !== false);
  const external = mode === 'external';
  const cartEnabled = mode === 'sweetspot' || mode === 'pickup';
  const orderCta = external ? readOrderCta(cfg) : null;

  const [cart, setCart] = useState<CartLine[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [modalProduct, setModalProduct] = useState<SiteProduct | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [placed, setPlaced] = useState<{ offerCount: number; orderToken?: string; paid?: boolean } | null>(null);
  const [activeCat, setActiveCat] = useState(0);
  const [menuPageOpen, setMenuPageOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const menuCloseRef = useRef<HTMLButtonElement>(null);
  const menuRestoreFocus = useRef<HTMLElement | null>(null);
  const viewTracked = useRef(false);

  const track = useCallback((
    eventType: 'site_view' | 'cta_click' | 'order_start',
    details?: { ctaKind?: 'external_order' | 'cart' | 'add_to_order'; productIds?: string[] },
  ) => {
    if (!analyticsEnabled) return;
    void siteApi.trackEvent(slug, eventType, details).catch(() => {
      // Analytics is deliberately best-effort and must not interrupt ordering.
    });
  }, [analyticsEnabled, slug]);

  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    track('site_view');
  }, [track]);

  useEffect(() => {
    if (photoPreview) { setHydrated(true); return; }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setCart(JSON.parse(raw));
    } catch {
      /* fresh cart */
    }
    setHydrated(true);
  }, [storageKey, photoPreview]);
  useEffect(() => {
    if (hydrated && !photoPreview) localStorage.setItem(storageKey, JSON.stringify(cart));
  }, [cart, hydrated, storageKey, photoPreview]);

  // A saved cart cannot make a removed or newly unpriced item orderable.
  useEffect(() => {
    setCart((current) => {
      const available = current.filter((line) => pricedProductIds.has(line.productId));
      return available.length === current.length ? current : available;
    });
  }, [cart, pricedProductIds]);

  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const subtotal = cart.reduce((s, l) => s + l.unitPrice * l.qty, 0);

  const addLine = useCallback((line: Omit<CartLine, 'key'>) => {
    if (!pricedProductIds.has(line.productId)) return;
    const key = `${line.productId}|${[...line.optionIds].sort().join(',')}`;
    setCart((cur) => {
      const i = cur.findIndex((l) => l.key === key);
      if (i === -1) return [...cur, { ...line, key }];
      const next = [...cur];
      next[i] = { ...next[i], qty: next[i].qty + line.qty };
      return next;
    });
    toast.success(`${line.title} added`, { duration: 1400 });
  }, [pricedProductIds]);

  const setQty = useCallback((key: string, qty: number) => {
    setCart((cur) =>
      qty <= 0 ? cur.filter((l) => l.key !== key) : cur.map((l) => (l.key === key ? { ...l, qty } : l)),
    );
  }, []);

  const quickAdd = useCallback(
    (p: SiteProduct) => {
      if (!hasProductPrice(p.price)) return;
      const groups = p.optionGroups ?? [];
      if (groups.some((g) => g.required)) {
        setModalProduct(p);
        return;
      }
      addLine({
        productId: p.id,
        title: p.title,
        unitPrice: Number(p.price),
        qty: 1,
        optionIds: [],
        optionLabels: [],
        imageUrl: p.imageUrl,
      });
    },
    [addLine],
  );

  /* scrollspy */
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  useEffect(() => {
    if (!showCategoryNav || (mallowEditorial && !menuPageOpen)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const idx = sectionRefs.current.indexOf(visible[0].target as HTMLElement);
          if (idx >= 0) setActiveCat(idx);
        }
      },
      { rootMargin: '-140px 0px -60% 0px' },
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [categories.length, mallowEditorial, menuPageOpen, showCategoryNav]);

  const scrollToCat = (i: number) => {
    sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openMenuPage = () => {
    if (!mallowEditorial) return scrollToCat(0);
    if (menuPageOpen) { menuCloseRef.current?.focus(); return; }
    menuRestoreFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setMenuPageOpen(true);
  };
  const openOrder = () => {
    if (mallowEditorial && cartCount === 0) return openMenuPage();
    track('cta_click', { ctaKind: 'cart' });
    setCartOpen(true);
  };
  const closeMenuPage = useCallback(() => setMenuPageOpen(false), []);
  useEffect(() => {
    if (!menuPageOpen) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    menuCloseRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') closeMenuPage(); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', onKeyDown);
      menuRestoreFocus.current?.focus();
    };
  }, [closeMenuPage, menuPageOpen]);

  const placeOrder = async (args: {
    when: string;
    note: string;
    guest: { name: string; email: string; phone: string };
    paymentMethodId?: string;
    onNextAction?: (clientSecret: string) => Promise<boolean>;
  }) => {
    if (photoPreview) throw new Error('Ordering is unavailable in the photo preview.');
    const items = cart.map((l) => ({
      productId: l.productId,
      quantity: l.qty,
      selectedOptionIds: l.optionIds.length ? l.optionIds : undefined,
    }));
    const res = await siteApi.placeOrder(slug, {
      items,
      fulfillment: { type: 'pickup', when: args.when },
      note: args.note.trim() || undefined,
      guest: args.guest,
      paymentMethodId: args.paymentMethodId,
    });
    // 3DS: finish the challenge in place; the webhook settles the final state.
    if (res.clientSecret && args.onNextAction) {
      const ok = await args.onNextAction(res.clientSecret);
      if (!ok) throw new Error('Card authentication was not completed.');
    }
    if (res.orderToken) {
      try {
        localStorage.setItem(`ss_site_order_${slug}`, res.orderToken);
      } catch {
        /* private mode — the link in the confirmation still works */
      }
    }
    setPlaced({ offerCount: res.offerCount ?? items.length, orderToken: res.orderToken, paid: res.paymentStatus === 'paid' });
    setCart([]);
    setCheckoutOpen(false);
    setCartOpen(false);
  };

  // Featured spotlight (siteConfig.featured): one hero item — big photo, eyebrow,
  // title, blurb, price — rendered between the info block and the menu.
  const featuredCfg = (cfg.featured ?? null) as { productId?: string; eyebrow?: string; title?: string; blurb?: string } | null;
  let featured: { product: SiteProduct; eyebrow: string; title: string; blurb: string } | null = null;
  if (featuredCfg?.productId) {
    for (const c of categories) {
      const hit = c.products.find((x) => x.id === featuredCfg.productId);
      if (hit) {
        featured = {
          product: hit,
          eyebrow: featuredCfg.eyebrow ?? 'Featured',
          title: featuredCfg.title ?? hit.title,
          blurb: featuredCfg.blurb ?? hit.description ?? '',
        };
        break;
      }
    }
  }
  const address = typeof cfg.address === 'string' ? cfg.address : business.addressStreet;
  const featuredImage = featured
    ? responsiveProductImageAttributes(featured.product.imageUrl, featured.product.imageVariants)
    : null;
  const headerOrderClass = `flex shrink-0 items-center gap-2 rounded-full px-4 font-medium transition-colors duration-200 active:scale-95 motion-reduce:transition-none ${overlayHeader ? 'h-8 text-xs min-[360px]:text-sm' : 'h-9 text-sm'} ${clearHeader
    ? 'bg-white text-black'
    : 'bg-[var(--s-primary)] text-[var(--s-primary-fg)]'}`;
  const headerLinkClass = 'inline-flex min-h-11 items-center text-xs hover:opacity-70 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 min-[360px]:text-sm';

  return (
    <div
      onClickCapture={photoPreview ? (event) => { if ((event.target as HTMLElement).closest('a')) event.preventDefault(); } : undefined}
      style={{ ...vars, fontFamily: 'var(--font-site-body)' }}
      className={`${mallowEditorial ? 'mallow-editorial' : ''} ${embedded ? '' : 'min-h-screen'} bg-[var(--s-bg)] text-[var(--s-ink)]`}
    >
      <style>{`.s-display{font-family:var(--font-site-display);font-style:italic;font-weight:600;letter-spacing:-0.01em}`}</style>

      {/* ── Sticky header ── */}
      <header className={overlayHeader
        ? `fixed inset-x-0 top-0 z-40 transition-colors duration-200 motion-reduce:transition-none ${clearHeader ? 'bg-transparent text-white' : 'bg-white text-black'}`
        : 'sticky top-0 z-40 border-b border-[var(--s-border)]/40 bg-white/80 backdrop-blur'}>
        <div className={overlayHeader
          ? `relative mx-4 flex h-[76px] items-center justify-between gap-3 border-b md:mx-6 md:h-[72px] ${clearHeader ? 'border-white/30' : 'border-black/10'}`
          : 'relative mx-auto flex h-14 max-w-6xl items-center justify-between px-4'}>
          {/* Full logo set: the compact mark + the wordmark (each optional on its own). */}
          {mallowEditorial ? <>
            <a href="#mallow-home" className="mallow-nav-wordmark" aria-label="Cafe Mallow home">
              <span aria-hidden="true" />
            </a>
            <span className="mallow-nav-monogram" aria-hidden="true" />
          </> : <span className={overlayHeader ? 'contents' : 'flex min-w-0 items-center gap-2.5'}>
            {!embedded && business.logoMarkUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={business.logoMarkUrl} alt="" className={`h-8 w-auto shrink-0 object-contain ${overlayHeader ? 'hidden md:block md:h-10' : ''} ${brandFilter}`} />
            )}
            {embedded ? <h2 className="text-xl font-semibold">Menu</h2> : business.logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={business.logoUrl} alt={displayName} className={`w-auto object-contain ${overlayHeader ? 'h-7 min-w-0 max-w-[100px] object-left sm:h-8 sm:max-w-[130px] md:absolute md:left-1/2 md:max-w-[180px] md:-translate-x-1/2 md:object-center' : 'h-6 max-w-[180px] shrink-0'} ${brandFilter}`} />
            ) : !business.logoMarkUrl || overlayHeader ? (
              <span className={`s-display text-2xl ${overlayHeader ? 'max-w-[100px] truncate md:absolute md:left-1/2 md:max-w-[180px] md:-translate-x-1/2' : ''}`}>{displayName.toLowerCase()}</span>
            ) : (
              <span className="sr-only">{displayName}</span>
            )}
          </span>}
          <div role={overlayHeader ? 'navigation' : undefined} aria-label={overlayHeader ? 'Store navigation' : undefined}
            className={overlayHeader ? 'ml-auto flex shrink-0 items-center gap-3 min-[360px]:gap-4 sm:gap-7' : undefined}>
          {overlayHeader && (
            <>
              <button ref={menuTriggerRef} type="button" className={headerLinkClass} onClick={openMenuPage}>Menu</button>
              <Link href={`/site/${encodeURIComponent(handle)}/about`} className={`${headerLinkClass} hidden sm:inline-flex`}>About</Link>
              {address && <LocationDialog name={displayName} address={address} mapEmbedUrl={cfg.mapEmbedUrl} className={headerLinkClass} />}
            </>
          )}
          {orderCta ? (
            <OrderCtaLink
              cta={orderCta}
              compact={overlayHeader}
              onClick={() => track('cta_click', { ctaKind: 'external_order' })}
              className={headerOrderClass}
            />
          ) : cartEnabled ? (
            <button
              type="button"
              onClick={openOrder}
              className={headerOrderClass}
            >
              {!overlayHeader && <ShoppingBag className="h-4 w-4" />}
              {overlayHeader ? `Order${cartCount > 0 ? ` (${cartCount})` : ''}` : cartCount > 0 ? `${cartCount} · ${money(subtotal)}` : 'Order'}
            </button>
          ) : (
            <span className={`text-xs font-medium ${clearHeader ? 'text-white' : 'text-[var(--s-muted)]'}`}>Menu only</span>
          )}
          </div>
        </div>
      </header>

      {promotions && (
        <div style={promotionVars}>
          <StorefrontPromotions tokens={promotions.tokens} sections={promotions.sections} />
        </div>
      )}

      {/* ── Sticky category nav ── */}
      {showCategoryNav && !mallowEditorial && <nav aria-label="Menu categories" className="sticky top-14 z-30 border-b border-[var(--s-border)]/40 bg-white/80 backdrop-blur">
        <div className="relative mx-auto flex max-w-6xl items-center gap-1 overflow-x-auto px-4 py-2 [scrollbar-width:none]">
          {categories.map((c, i) => (
            <button
              key={c.name}
              type="button"
              onClick={() => scrollToCat(i)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                i === activeCat
                  ? 'bg-[var(--s-primary)] font-semibold text-[var(--s-primary-fg)]'
                  : 'text-[var(--s-ink)]/80 hover:bg-[var(--s-border)]/50'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </nav>}

      {/* ── Hero and store information ── */}
      {mallowEditorial && <MallowEditorialHero name={displayName} onMenu={openMenuPage} aboutHref={`/site/${encodeURIComponent(handle)}/about`} address={address} />}
      {heroImage && !mallowEditorial && (
        <div className="relative">
          {photoHero && <h1 className="sr-only">{displayName}</h1>}
          <picture className="block">
            {photoHero && heroMobileImage && <source media="(max-width: 767px)" srcSet={heroMobileImage} />}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={heroImage}
              alt=""
              fetchPriority="high"
              style={{ objectPosition: cfg.heroPosition, '--photo-mobile-position': cfg.heroMobilePosition ?? cfg.heroPosition ?? 'bottom' } as React.CSSProperties}
              className={photoHero
                ? `w-full object-cover object-bottom max-md:![object-position:var(--photo-mobile-position)] ${heroMobileImage ? 'aspect-[2/3]' : 'aspect-[4/3]'} ${photoBanner ? 'md:aspect-[4/3]' : 'md:aspect-auto md:h-[min(780px,calc(100svh-56px))] md:min-h-[620px]'}`
                : 'h-[300px] w-full object-cover md:aspect-[2/1] md:h-auto md:max-h-[560px]'}
            />
          </picture>
        </div>
      )}

      {photoBanner && !mallowEditorial && (
        <section aria-label="Store highlight" className="relative isolate text-white">
          <picture className="block">
            {typeof photoBanner.mobileImage === 'string' && <source media="(max-width: 767px)" srcSet={photoBanner.mobileImage} />}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={photoBanner.image} alt="" loading="lazy" decoding="async"
              className="aspect-[2/3] w-full object-cover max-md:![object-position:var(--photo-mobile-position)] md:aspect-[5/4]"
              style={{ objectPosition: photoBanner.position, '--photo-mobile-position': photoBanner.mobilePosition ?? photoBanner.position ?? 'center' } as React.CSSProperties} />
          </picture>
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-1/2"
            style={{ background: `linear-gradient(to bottom, rgb(0 0 0 / ${photoBanner.gradient ?? 0.45}), transparent)` }} />
          <div className="absolute inset-x-0 top-0 px-6 py-12 min-[360px]:px-8 md:px-12 md:py-16">
            <h2 className="max-w-[22ch] text-2xl font-light uppercase leading-[1.2] tracking-wide min-[360px]:text-[clamp(1.75rem,3.2vw,3.5rem)]">
              {photoBanner.title ?? (typeof cfg.tagline === 'string' && cfg.tagline ? cfg.tagline : displayName)}
            </h2>
            {bannerCta || (photoBanner.buttonTarget !== 'menu' && orderCta) ? (
              <OrderCtaLink cta={bannerCta ?? orderCta!}
                onClick={() => track('cta_click', { ctaKind: 'external_order' })}
                className="mt-6 inline-flex min-h-12 items-center gap-3 border-2 border-white px-6 py-3 text-base font-semibold uppercase tracking-wide transition-colors hover:bg-white hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 motion-reduce:transition-none md:mt-8 md:text-lg">
                {photoBanner.buttonLabel || 'Order now'}
              </OrderCtaLink>
            ) : categories.length > 0 && (
              <button type="button" onClick={() => scrollToCat(0)}
                className="mt-6 inline-flex min-h-12 items-center gap-3 border-2 border-white px-6 py-3 text-base font-semibold uppercase tracking-wide transition-colors hover:bg-white hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 motion-reduce:transition-none md:mt-8 md:text-lg">
                {photoBanner.buttonLabel || (cartEnabled ? 'Order now' : 'View menu')} <ArrowUpRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </section>
      )}

      {/* ── Info block (photo heroes keep brand/order controls in the header) ── */}
      {!embedded && !photoHero && !mallowEditorial && <section className="mx-auto max-w-6xl px-4 pb-2 pt-8 text-center">
        {business.logoUrl ? (
          <h1 className="flex justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={business.logoUrl} alt={displayName} className="h-14 w-auto max-w-[80vw] object-contain md:h-20" />
            <span className="sr-only">{displayName}</span>
          </h1>
        ) : (
          <h1 className="s-display text-5xl md:text-6xl">{displayName.toLowerCase()}</h1>
        )}
        {typeof cfg.tagline === 'string' && (
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[var(--s-muted)]">
            {cfg.tagline}
          </p>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-sm text-[var(--s-muted)]">
          {address && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {address}
            </span>
          )}
          {openState.known && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span className={openState.open ? 'font-medium text-green-700' : 'font-medium text-amber-700'}>
                {openState.label}
              </span>
            </span>
          )}
        </div>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--s-border)] bg-[var(--s-surface)] px-1.5 py-1.5 text-sm">
          <span className="rounded-full bg-[var(--s-primary)] px-4 py-1 font-semibold text-[var(--s-primary-fg)]">
            Pickup
          </span>
          <span className="px-3 text-[var(--s-muted)]">
            {openState.open ? 'Ready in ~15 min' : 'Order ahead for opening'}
          </span>
        </div>
      </section>}

      {/* ── Featured spotlight ── */}
      {featured && !mallowEditorial && (
        <section className="mx-auto max-w-6xl px-4 pt-12">
          <div className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
            <InstagramProductPhoto photo={featured.product.instagramPhoto} title={featured.product.title}
              onImageClick={() => setModalProduct(featured.product)}
              className="group block w-full overflow-hidden rounded-[calc(var(--s-radius)*2)]"
              imageClassName="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]">
              <div className="overflow-hidden rounded-[calc(var(--s-radius)*2)] bg-[var(--s-surface)]">
                {featuredImage?.src && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={featuredImage.src}
                    srcSet={featuredImage.srcSet}
                    sizes="(max-width: 767px) calc(100vw - 2rem), 50vw"
                    alt=""
                    className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                )}
              </div>
            </InstagramProductPhoto>
            <div className="text-center md:text-left">
              <p className="s-display text-xl text-[var(--s-muted)] md:text-2xl">{featured.eyebrow}</p>
              <h2 className="s-display mt-1 text-4xl md:text-5xl">{featured.title}</h2>
              {featured.blurb && (
                <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-[var(--s-muted)] md:mx-0">{featured.blurb}</p>
              )}
              <SecondaryProductCopy
                primaryName={featured.title}
                primaryDescription={featured.blurb}
                name2={featured.product.name2}
                description2={featured.product.description2}
                className="mx-auto mt-3 max-w-md md:mx-0"
                nameClassName="text-base font-semibold"
                descriptionClassName="text-[15px] leading-relaxed text-[var(--s-muted)]"
              />
              <div className="mt-6 flex items-center justify-center gap-4 md:justify-start">
                {hasProductPrice(featured.product.price) && (
                  <span className="text-lg font-semibold">{money(Number(featured.product.price))}</span>
                )}
                {!hasProductPrice(featured.product.price) ? (
                  <ComingSoonButton />
                ) : orderCta ? (
                  <OrderCtaLink
                    cta={orderCta}
                    onClick={() => track('cta_click', { ctaKind: 'external_order', productIds: [featured!.product.id] })}
                    className="flex h-10 items-center gap-2 rounded-full bg-[var(--s-primary)] px-5 text-sm font-semibold text-[var(--s-primary-fg)] active:scale-95"
                  />
                ) : cartEnabled ? (
                  <button
                    type="button"
                    onClick={() => {
                      track('cta_click', { ctaKind: 'add_to_order', productIds: [featured!.product.id] });
                      quickAdd(featured!.product);
                    }}
                    className="flex h-10 items-center gap-2 rounded-full bg-[var(--s-primary)] px-5 text-sm font-semibold text-[var(--s-primary-fg)] active:scale-95"
                  >
                    <Plus className="h-4 w-4" />
                    Add to order
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      )}


      {/* ── Menu sections ── */}
      <div className={mallowEditorial ? 'mallow-menu-page' : undefined}
        role={mallowEditorial ? 'dialog' : undefined} aria-modal={mallowEditorial ? true : undefined}
        aria-labelledby={mallowEditorial ? 'mallow-menu-title' : undefined} hidden={mallowEditorial && !menuPageOpen}>
      {mallowEditorial && <>
        <div className="mallow-menu-toolbar">
          <h2 id="mallow-menu-title">Cafe Mallow Menu</h2>
          <div>
            {orderCta ? <OrderCtaLink cta={orderCta} compact className="mallow-menu-order" /> : cartEnabled &&
              <button type="button" className="mallow-menu-order" onClick={openOrder}>Order{cartCount ? ` (${cartCount})` : ''}</button>}
            <button ref={menuCloseRef} type="button" onClick={closeMenuPage}>Close menu <X aria-hidden="true" className="h-4 w-4" /></button>
          </div>
        </div>
        <nav className="mallow-menu-categories" aria-label="Menu categories">
          {categories.map((category, index) => <button key={category.name} type="button" aria-current={activeCat === index ? 'true' : undefined}
            onClick={() => scrollToCat(index)}>{category.name}</button>)}
        </nav>
      </>}
      <main id="menu" className={`mx-auto max-w-6xl scroll-mt-24 px-4 ${orderCta ? 'pb-28 md:pb-20' : 'pb-20'}`}>
        {categories.map((cat, i) => (
          <section
            key={cat.name}
            ref={(el) => {
              sectionRefs.current[i] = el;
            }}
            className="scroll-mt-32 pt-10"
          >
            <h2 className="s-display mb-4 text-3xl">{cat.name}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {cat.products.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onOpen={() => setModalProduct(p)}
                  onQuickAdd={cartEnabled ? () => {
                    track('cta_click', { ctaKind: 'add_to_order', productIds: [p.id] });
                    quickAdd(p);
                  } : undefined}
                />
              ))}
            </div>
          </section>
        ))}

        {!embedded && !mallowEditorial && <PublicInstagramGallery channel="site" handle={handle} className="mt-14 border-t border-[var(--s-border)] pt-10" />}
        {!embedded && !mallowEditorial && <PublicInstagramComments channel="site" handle={handle} className="mt-14 border-t border-[var(--s-border)] pt-10" />}

        {/* ── Story ── */}
        {!embedded && !mallowEditorial && typeof cfg.story === 'string' && (
          <section className="mx-auto mt-20 max-w-2xl border-t border-[var(--s-border)] pt-14 text-center">
            <p className="s-display text-2xl leading-relaxed text-[var(--s-ink)]/90">{cfg.story}</p>
            <Link href={`/site/${encodeURIComponent(handle)}/about`} className="mt-6 inline-flex min-h-11 items-center text-sm underline underline-offset-4">About {displayName} ↗</Link>
          </section>
        )}
      </main>
      </div>

      {/* ── Sticky order bar (external order button, phones only) ── */}
      {orderCta && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--s-border)]/60 bg-white/90 px-4 pt-3 backdrop-blur md:hidden"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <OrderCtaLink
            cta={orderCta}
            onClick={() => track('cta_click', { ctaKind: 'external_order' })}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--s-primary)] text-sm font-bold text-[var(--s-primary-fg)] active:scale-[0.98]"
          />
        </div>
      )}

      {/* ── Footer ── */}
      {!embedded && <footer className={`${mallowEditorial ? 'mallow-site-footer ' : ''}border-t border-[var(--s-border)] bg-[var(--s-surface)]`}>
        {mallowEditorial && <div className="mallow-footer-intro">
          <p>A little everyday comfort. Specialty coffee, thoughtfully made matcha, and something just sweet enough.</p>
          <Link href={`/site/${encodeURIComponent(handle)}/about`} className="mallow-text-link">About Cafe Mallow <span aria-hidden="true">↗</span></Link>
        </div>}
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 text-sm sm:grid-cols-3">
          <div>
            <h3 id={mallowEditorial ? "mallow-visit" : undefined} className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em]">Visit</h3>
            {!mallowEditorial && <Link href={`/site/${encodeURIComponent(handle)}/about`} className="mb-2 inline-flex min-h-11 items-center underline underline-offset-4">About {displayName}</Link>}
            <p className="text-[var(--s-muted)]">{address}</p>
            {typeof cfg.phone === 'string' && <p className="mt-1 text-[var(--s-muted)]">{cfg.phone}</p>}
          </div>
          <div>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em]">Hours</h3>
            {hours ? (
              <div className="space-y-0.5 text-[var(--s-muted)]">
                {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((d) => (
                  <div key={d} className="flex justify-between gap-6">
                    <span className="capitalize">{d}</span>
                    <span>
                      {hours[d] ? `${fmtTime(hours[d][0])} – ${fmtTime(hours[d][1])}` : 'Closed'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--s-muted)]">See store</p>
            )}
          </div>
          <div>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em]">Follow</h3>
            {(mallowEditorial || business.handleInstagram) && (
              <a
                href={`https://www.instagram.com/${mallowEditorial ? 'cafemallow_la' : business.handleInstagram}/`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[var(--s-muted)] hover:text-[var(--s-ink)]"
              >
                <Instagram className="h-4 w-4" /> @{mallowEditorial ? 'cafemallow_la' : business.handleInstagram}
              </a>
            )}
          </div>
        </div>
        <div className="border-t border-[var(--s-border)] py-5 text-center text-xs text-[var(--s-muted)]">
          © 2026 {displayName}
          {/* The API forces this on for Free and honours the saved toggle on Plus/Pro. */}
          {cfg.poweredBy !== false && (
            <>
              {' · '}
              <a
                href="https://www.sweetspot.la"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-[var(--s-border)] underline-offset-2 hover:text-[var(--s-ink)]"
              >
                Powered by Sweetspot
              </a>
            </>
          )}
          {/* Absolute, because on a custom store domain every path is rewritten
              into /site/<slug> — a relative /terms would 404 there. */}
          <span className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            {[
              ['Terms', 'https://www.sweetspot.la/terms'],
              ['Refunds', 'https://www.sweetspot.la/terms#refunds'],
              ['Privacy', 'https://www.sweetspot.la/privacy'],
            ].map(([label, href], i) => (
              <span key={href} className="flex items-center gap-x-2">
                {i > 0 && <span aria-hidden="true">·</span>}
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-[var(--s-border)] underline-offset-2 hover:text-[var(--s-ink)]"
                >
                  {label}
                </a>
              </span>
            ))}
          </span>
        </div>
      </footer>}

      {/* ── Overlays ── */}
      {modalProduct && (
        <ItemModal
          product={categories.flatMap((category) => category.products).find((p) => p.id === modalProduct.id)
            ?? { ...modalProduct, instagramPhoto: null }}
          orderCta={orderCta}
          cartEnabled={cartEnabled}
          onCtaClick={() => track('cta_click', { ctaKind: 'external_order', productIds: [modalProduct.id] })}
          onClose={() => setModalProduct(null)}
          onAdd={(line) => {
            track('cta_click', { ctaKind: 'add_to_order', productIds: [line.productId] });
            addLine(line);
            setModalProduct(null);
          }}
        />
      )}
      {cartEnabled && cartOpen && (
        <CartSheet
          lines={cart}
          subtotal={subtotal}
          storeName={displayName}
          address={address}
          openLabel={openState.label}
          onClose={() => setCartOpen(false)}
          onSetQty={setQty}
          onExploreMenu={mallowEditorial ? () => {
            setCartOpen(false);
            if (!menuPageOpen) menuTriggerRef.current?.focus();
            openMenuPage();
          } : undefined}
          onCheckout={() => {
            if (photoPreview) return;
            track('order_start', { productIds: [...new Set(cart.map((line) => line.productId))] });
            setCheckoutOpen(true);
          }}
        />
      )}
      {cartEnabled && checkoutOpen && (
        <CheckoutSheet
          subtotal={subtotal}
          count={cartCount}
          address={address}
          isOpen={openState.open}
          openLabel={openState.label}
          acceptsCards={acceptsCards}
          theme={{
            bg: String(vars['--s-surface' as keyof typeof vars] ?? '#FDFBF5'),
            ink: String(vars['--s-ink' as keyof typeof vars] ?? '#1D1A16'),
            primary: String(vars['--s-primary' as keyof typeof vars] ?? '#1D1A16'),
            radius: Number(theme.radius ?? 10),
          }}
          onClose={() => setCheckoutOpen(false)}
          onPlace={placeOrder}
        />
      )}
      {placed && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-[var(--s-surface)] p-8 text-center shadow-2xl">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Check className="h-6 w-6 text-green-700" />
            </span>
            <h3 className="s-display mt-4 text-3xl">Order received</h3>
            <p className="mt-2 text-sm text-[var(--s-muted)]">
              {displayName} got your pickup order ({placed.offerCount}{' '}
              {placed.offerCount === 1 ? 'item' : 'items'}){placed.paid ? ' and your card was charged' : ''}. We&apos;ll
              text you when it&apos;s ready.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <a
                href={placed.orderToken ? `/site/${slug}/order/${placed.orderToken}` : '/orders'}
                className="rounded-full bg-[var(--s-primary)] py-2.5 text-sm font-semibold text-[var(--s-primary-fg)]"
              >
                Track order status
              </a>
              <button
                type="button"
                onClick={() => setPlaced(null)}
                className="rounded-full border border-[var(--s-border)] py-2.5 text-sm font-medium"
              >
                Back to menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── External order button (siteConfig.orderCta) ─────────────────────── */

/**
 * The one button the Free plan promises: it leads to wherever the seller
 * already takes orders. Links and Instagram open a new tab; tel:/mailto:
 * hand off in place.
 */
function OrderCtaLink({
  cta,
  compact = false,
  className,
  children,
  onClick,
}: {
  cta: OrderCta;
  compact?: boolean;
  className: string;
  children?: ReactNode;
  onClick?: () => void;
}) {
  const href = orderCtaHref(cta);
  if (!href) return null;
  const Icon =
    cta.type === 'instagram' ? Instagram : cta.type === 'phone' ? Phone : cta.type === 'email' ? Mail : ArrowUpRight;
  return (
    <a href={href} onClick={onClick} aria-label={compact ? orderCtaLabel(cta) : undefined} {...(orderCtaOpensNewTab(cta) ? { target: '_blank', rel: 'noopener' } : {})} className={className}>
      {!compact && <Icon className="h-4 w-4" />}
      {compact ? 'Order' : children ?? orderCtaLabel(cta)}
    </a>
  );
}

/* ── Product card ─────────────────────────────────────────────────────── */

function ComingSoonButton() {
  return (
    <button
      type="button"
      disabled
      className="cursor-not-allowed rounded-full bg-[var(--s-border)] px-5 py-3 text-sm font-semibold text-[var(--s-muted)]"
    >
      Coming soon
    </button>
  );
}

function ProductCard({
  product: p,
  onOpen,
  onQuickAdd,
}: {
  product: SiteProduct;
  onOpen: () => void;
  /** Absent when the site has no cart (external order button). */
  onQuickAdd?: () => void;
}) {
  const image = responsiveProductImageAttributes(p.imageUrl, p.imageVariants);
  const priced = hasProductPrice(p.price);
  return (
    <div
      className="group cursor-pointer overflow-hidden rounded-[var(--s-radius)] border border-[var(--s-border)] bg-[var(--s-surface)] transition-shadow hover:shadow-md"
      onClick={(event) => { if (!(event.target as HTMLElement).closest('a, button')) onOpen(); }}
    >
      <div className="relative aspect-[4/3] bg-[var(--s-bg)]">
        <InstagramProductPhoto photo={p.instagramPhoto} title={p.title} onImageClick={onOpen}
          className="h-full w-full" imageClassName="h-full w-full object-contain">
          {image.src ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={image.src}
              srcSet={image.srcSet}
              sizes="(max-width: 767px) 50vw, (max-width: 1023px) 33vw, 25vw"
              alt={p.title}
              className="h-full w-full object-contain"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <span className="s-display text-4xl text-[var(--s-border)]">{p.title.charAt(0).toLowerCase()}</span>
            </div>
          )}
        </InstagramProductPhoto>
        {onQuickAdd && (
          <button
            type="button"
            aria-label={`Add ${p.title}`}
            disabled={!priced}
            onClick={(e) => {
              e.stopPropagation();
              onQuickAdd();
            }}
            className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full border border-[var(--s-border)] bg-white text-[var(--s-ink)] shadow-sm transition-transform active:scale-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold leading-tight"><button type="button" onClick={onOpen} className="text-left">{p.title}</button></h3>
        {p.description && (
          <p className="mt-1 line-clamp-2 text-xs text-[var(--s-muted)]">{p.description}</p>
        )}
        <SecondaryProductCopy
          primaryName={p.title}
          primaryDescription={p.description}
          name2={p.name2}
          description2={p.description2}
          className="mt-1"
          nameClassName="text-xs font-medium leading-tight"
          descriptionClassName="line-clamp-2 text-xs text-[var(--s-muted)]"
        />
        <p className="mt-1.5 text-[13px] font-medium">
          {priced ? money(Number(p.price)) : <span className="text-[var(--s-muted)]">Coming soon</span>}
        </p>
      </div>
    </div>
  );
}

/* ── Item modal (option groups, teardown §4) ──────────────────────────── */

function ItemModal({
  product: p,
  orderCta,
  cartEnabled = true,
  onCtaClick,
  onClose,
  onAdd,
}: {
  product: SiteProduct;
  /** Set when the site has no cart: the footer becomes the external order button. */
  orderCta?: OrderCta | null;
  cartEnabled?: boolean;
  onCtaClick?: () => void;
  onClose: () => void;
  onAdd: (line: Omit<CartLine, 'key'>) => void;
}) {
  const groups = p.optionGroups ?? [];
  const image = responsiveProductImageAttributes(p.imageUrl, p.imageVariants);
  const [selected, setSelected] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    for (const g of groups) {
      if (g.required) {
        const def = g.options.find((o) => o.isDefault) ?? g.options[0];
        init[g.id] = def ? [def.id] : [];
      } else {
        init[g.id] = [];
      }
    }
    return init;
  });
  const [qty, setQty] = useState(1);
  const [touchedAdd, setTouchedAdd] = useState(false);

  const toggle = (g: SiteOptionGroup, optId: string) => {
    setSelected((cur) => {
      const c = cur[g.id] ?? [];
      if (g.required && g.maxSelect <= 1) return { ...cur, [g.id]: [optId] };
      if (c.includes(optId)) return { ...cur, [g.id]: c.filter((x) => x !== optId) };
      if (c.length >= g.maxSelect) {
        if (g.maxSelect === 1) return { ...cur, [g.id]: [optId] };
        return cur;
      }
      return { ...cur, [g.id]: [...c, optId] };
    });
  };

  const allOptions = useMemo(() => {
    const m = new Map<string, SiteOption & { groupName: string }>();
    for (const g of groups) for (const o of g.options) m.set(o.id, { ...o, groupName: g.name });
    return m;
  }, [groups]);

  const chosenIds = Object.values(selected).flat();
  const delta = chosenIds.reduce((s, id) => s + Number(allOptions.get(id)?.priceDelta ?? 0), 0);
  const unit = Number(p.price) + delta;
  const priced = hasProductPrice(p.price);
  const missing = groups.filter((g) => g.required && (selected[g.id]?.length ?? 0) < g.minSelect);

  const add = () => {
    if (!priced) return;
    if (missing.length) {
      setTouchedAdd(true);
      toast.error(`Please choose: ${missing.map((g) => g.name).join(', ')}`);
      return;
    }
    onAdd({
      productId: p.id,
      title: p.title,
      unitPrice: unit,
      qty,
      optionIds: chosenIds,
      optionLabels: chosenIds.map((id) => allOptions.get(id)?.name ?? '').filter(Boolean),
      imageUrl: p.imageUrl,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-t-2xl bg-[var(--s-surface)] shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <InstagramProductPhoto photo={p.instagramPhoto} title={p.title} imageClassName="h-52 w-full object-contain">
            {image.src && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={image.src}
                srcSet={image.srcSet}
                sizes="(max-width: 639px) 100vw, 448px"
                alt={p.title}
                className="h-52 w-full object-contain"
              />
            )}
          </InstagramProductPhoto>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4 pt-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-xl font-bold">{p.title}</h2>
            {priced && <span className="text-sm font-medium text-[var(--s-muted)]">{money(Number(p.price))}</span>}
          </div>
          {p.description && <p className="mt-1.5 text-sm text-[var(--s-muted)]">{p.description}</p>}
          <SecondaryProductCopy
            primaryName={p.title}
            primaryDescription={p.description}
            name2={p.name2}
            description2={p.description2}
            className="mt-2"
            nameClassName="text-sm font-semibold"
            descriptionClassName="text-sm text-[var(--s-muted)]"
          />

          {priced && groups.map((g) => {
            const isMissing = touchedAdd && g.required && (selected[g.id]?.length ?? 0) < g.minSelect;
            const single = g.maxSelect <= 1;
            return (
              <div key={g.id} className="mt-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold">{g.name}</h3>
                  <span
                    className={`text-[11px] font-medium ${
                      isMissing ? 'text-red-600' : g.required ? 'text-green-700' : 'text-[var(--s-muted)]'
                    }`}
                  >
                    {g.required ? `Required · Select ${g.maxSelect}` : `Optional · up to ${g.maxSelect}`}
                  </span>
                </div>
                <div className="mt-2 divide-y divide-[var(--s-border)] rounded-[var(--s-radius)] border border-[var(--s-border)]">
                  {g.options.map((o) => {
                    const on = (selected[g.id] ?? []).includes(o.id);
                    const d = Number(o.priceDelta);
                    return (
                      <label
                        key={o.id}
                        className="flex cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5 text-sm"
                      >
                        <span className="flex items-center gap-2.5">
                          <input
                            type={g.required && single ? 'radio' : 'checkbox'}
                            name={g.id}
                            checked={on}
                            onChange={() => toggle(g, o.id)}
                            className="h-4 w-4 accent-[var(--s-primary)]"
                          />
                          {o.name}
                        </span>
                        {d > 0 && <span className="text-xs text-[var(--s-muted)]">+{money(d)}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 border-t border-[var(--s-border)] px-5 py-3.5">
          {!priced ? (
            <div className="flex flex-1 justify-center"><ComingSoonButton /></div>
          ) : orderCta ? (
            <OrderCtaLink
              cta={orderCta}
              onClick={onCtaClick}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--s-primary)] py-3 text-sm font-bold text-[var(--s-primary-fg)] active:scale-[0.98]"
            />
          ) : cartEnabled ? (
            <>
              <div className="flex items-center rounded-full border border-[var(--s-border)]">
                <button type="button" aria-label="Decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-6 text-center text-sm font-semibold">{qty}</span>
                <button type="button" aria-label="Increase" onClick={() => setQty(qty + 1)} className="p-2.5">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                type="button"
                onClick={add}
                className="flex-1 rounded-full bg-[var(--s-primary)] py-3 text-sm font-bold text-[var(--s-primary-fg)] active:scale-[0.98]"
              >
                Add to order — {money(unit * qty)}
              </button>
            </>
          ) : (
            <p className="flex-1 py-2 text-center text-sm text-[var(--s-muted)]">Ordering is not available on this page.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Cart sheet (teardown §5) ─────────────────────────────────────────── */

function CartSheet({
  lines,
  subtotal,
  storeName,
  address,
  openLabel,
  onClose,
  onSetQty,
  onExploreMenu,
  onCheckout,
}: {
  lines: CartLine[];
  subtotal: number;
  storeName: string;
  address?: string | null;
  openLabel: string;
  onClose: () => void;
  onSetQty: (key: string, qty: number) => void;
  onExploreMenu?: () => void;
  onCheckout: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/45" onClick={onClose}>
      <div
        role="dialog" aria-modal="true" aria-label="Your order"
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-[var(--s-surface)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--s-border)] px-5 py-4">
          <h2 className="text-lg font-bold">Your order</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded-full p-1.5 hover:bg-[var(--s-bg)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mx-5 mt-4 rounded-[var(--s-radius)] border border-[var(--s-border)] bg-[var(--s-bg)] px-4 py-3 text-sm">
          <p className="font-semibold">Pickup · {storeName}</p>
          {address && <p className="mt-0.5 text-xs text-[var(--s-muted)]">{address}</p>}
          {openLabel && <p className="mt-0.5 text-xs text-[var(--s-muted)]">{openLabel}</p>}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {lines.length === 0 ? (
            <p className="pt-10 text-center text-sm text-[var(--s-muted)]">Your order is empty.</p>
          ) : (
            <ul className="divide-y divide-[var(--s-border)]">
              {lines.map((l) => (
                <li key={l.key} className="flex gap-3 py-3">
                  {l.imageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={l.imageUrl} alt="" className="h-14 w-14 rounded-[var(--s-radius)] object-cover" />
                  ) : (
                    <div className="h-14 w-14 rounded-[var(--s-radius)] bg-[var(--s-bg)]" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{l.title}</p>
                    {l.optionLabels.length > 0 && (
                      <p className="truncate text-xs text-[var(--s-muted)]">{l.optionLabels.join(', ')}</p>
                    )}
                    <p className="mt-0.5 text-sm">{money(l.unitPrice * l.qty)}</p>
                  </div>
                  <div className="flex items-center gap-1 self-center">
                    <button
                      type="button"
                      aria-label="Decrease"
                      onClick={() => onSetQty(l.key, l.qty - 1)}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-[var(--s-border)]"
                    >
                      {l.qty === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                    </button>
                    <span className="w-5 text-center text-sm font-semibold">{l.qty}</span>
                    <button
                      type="button"
                      aria-label="Increase"
                      onClick={() => onSetQty(l.key, l.qty + 1)}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-[var(--s-border)]"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {onExploreMenu && <button type="button" onClick={onExploreMenu}
            className="mt-4 flex min-h-11 w-full items-center justify-between rounded-full border border-[var(--s-border)] px-4 py-3 text-sm font-medium hover:bg-[var(--s-bg)]">
            Explore the menu <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
          </button>}
        </div>
        <div className="border-t border-[var(--s-border)] px-5 py-4">
          <button
            type="button"
            disabled={lines.length === 0}
            onClick={onCheckout}
            className="flex w-full items-center justify-between rounded-full bg-[var(--s-primary)] px-6 py-3.5 text-sm font-bold text-[var(--s-primary-fg)] disabled:opacity-40"
          >
            <span>Checkout</span>
            <span>{money(subtotal)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Checkout sheet — guest contact + card (teardown §6, plan §3–4) ───── */

function CheckoutSheet({
  subtotal,
  count,
  address,
  isOpen,
  openLabel,
  acceptsCards,
  theme,
  onClose,
  onPlace,
}: {
  subtotal: number;
  count: number;
  address?: string | null;
  isOpen: boolean;
  openLabel: string;
  acceptsCards: boolean;
  theme: { bg: string; ink: string; primary: string; radius: number };
  onClose: () => void;
  onPlace: (args: {
    when: string;
    note: string;
    guest: { name: string; email: string; phone: string };
    paymentMethodId?: string;
    onNextAction?: (clientSecret: string) => Promise<boolean>;
  }) => Promise<void>;
}) {
  const [when, setWhen] = useState(isOpen ? 'asap' : 'opening');
  const [note, setNote] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const payRef = useRef<PaymentFormHandle>(null);

  // Remember the customer between orders on this device — guests should not
  // retype their details every time (plan §4).
  useEffect(() => {
    try {
      const raw = localStorage.getItem('ss_site_guest');
      if (raw) {
        const g = JSON.parse(raw);
        setName(g.name ?? '');
        setEmail(g.email ?? '');
        setPhone(g.phone ?? '');
      }
    } catch {
      /* ignore */
    }
  }, []);

  const contactValid =
    name.trim().length > 0 && /.+@.+\..+/.test(email.trim()) && phone.replace(/\D/g, '').length >= 7;

  const submit = async () => {
    if (!contactValid) {
      toast.error('Please fill in your name, email and phone.');
      return;
    }
    setBusy(true);
    try {
      let paymentMethodId: string | undefined;
      if (acceptsCards) {
        const pm = await payRef.current?.createPaymentMethod();
        if (!pm) return; // the form already showed why
        paymentMethodId = pm;
      }
      const guest = { name: name.trim(), email: email.trim(), phone: phone.trim() };
      try {
        localStorage.setItem('ss_site_guest', JSON.stringify(guest));
      } catch {
        /* ignore */
      }
      await onPlace({
        when,
        note,
        guest,
        paymentMethodId,
        onNextAction: (cs) => payRef.current?.handleNextAction(cs) ?? Promise.resolve(false),
      });
    } catch (e: any) {
      toast.error(String(e?.message ?? 'Could not place the order — please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const field =
    'w-full rounded-[var(--s-radius)] border border-[var(--s-border)] bg-[var(--s-bg)] px-3 py-2.5 text-sm outline-none focus:border-[var(--s-primary)]';

  return (
    <div
      className="fixed inset-0 z-[55] flex items-end justify-center overflow-y-auto bg-black/45 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="my-auto w-full max-w-md rounded-t-2xl bg-[var(--s-surface)] p-6 shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Pickup details</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded-full p-1.5 hover:bg-[var(--s-bg)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        {address && <p className="mt-1.5 text-sm text-[var(--s-muted)]">{address}</p>}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={!isOpen}
            onClick={() => setWhen('asap')}
            className={`rounded-[var(--s-radius)] border px-3 py-3 text-left text-sm ${
              when === 'asap' ? 'border-[var(--s-primary)] bg-[var(--s-bg)] font-semibold' : 'border-[var(--s-border)]'
            } disabled:opacity-40`}
          >
            ASAP
            <span className="block text-xs font-normal text-[var(--s-muted)]">
              {isOpen ? 'Ready in ~15 min' : 'Unavailable — closed'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setWhen('opening')}
            className={`rounded-[var(--s-radius)] border px-3 py-3 text-left text-sm ${
              when === 'opening' ? 'border-[var(--s-primary)] bg-[var(--s-bg)] font-semibold' : 'border-[var(--s-border)]'
            }`}
          >
            At opening
            <span className="block text-xs font-normal text-[var(--s-muted)]">{openLabel || 'Next open time'}</span>
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--s-muted)]">Your details</h3>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name for the order" className={field} autoComplete="name" />
          <div className="grid grid-cols-2 gap-2">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" className={field} autoComplete="email" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" type="tel" className={field} autoComplete="tel" />
          </div>
          <p className="text-[11px] text-[var(--s-muted)]">No account needed — we use this to text you when it&apos;s ready.</p>
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={400}
          placeholder="Note for the cafe (allergies, etc.)"
          className={`${field} mt-3 resize-none`}
        />

        {acceptsCards && (
          <div className="mt-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--s-muted)]">Payment</h3>
            <PaymentForm ref={payRef} amountCents={Math.round(subtotal * 100)} theme={theme} onError={(m) => toast.error(m)} />
          </div>
        )}

        <div className="mt-4 flex justify-between text-sm">
          <span className="text-[var(--s-muted)]">
            {count} {count === 1 ? 'item' : 'items'}
            {acceptsCards ? '' : ' · pay at pickup'}
          </span>
          <span className="font-bold">{money(subtotal)}</span>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="mt-3 flex w-full items-center justify-center gap-1 rounded-full bg-[var(--s-primary)] py-3.5 text-sm font-bold text-[var(--s-primary-fg)] disabled:opacity-50"
        >
          {busy ? 'Placing order…' : acceptsCards ? `Pay ${money(subtotal)} · Place order` : 'Place pickup order'}
          {!busy && <ChevronRight className="h-4 w-4" />}
        </button>
        <p className="mt-2 text-center text-[11px] text-[var(--s-muted)]">
          {acceptsCards
            ? 'Charged now. Full refund if the cafe cannot take the order.'
            : 'Payment is taken at the counter.'}
        </p>
      </div>
    </div>
  );
}
